import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    const res = await register(email, password, name);
    setBusy(false);
    if (res.ok) {
      toast.success("Your archive has been opened.");
      nav("/app", { replace: true });
    } else {
      setError(res.error);
    }
  }

  return (
    <section className="min-h-[80vh] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative border-r border-[color:var(--ink-line)] bg-[color:var(--bone-2)] p-14 flex-col justify-between">
        <div>
          <div className="overline mb-6">Volume II · Begin the Ledger</div>
          <h2 className="font-serif-display text-4xl lg:text-5xl leading-[0.95] tracking-tight">
            Open a chapter for your family.
          </h2>
          <p className="mt-6 font-italic-serif italic text-lg text-[color:var(--ink-soft)] max-w-md">
            "A family tree is a museum where every visitor is also an exhibit."
          </p>
        </div>
        <div className="numeric-index text-[color:var(--ink-soft)]">
          II / II · Create · Est. 2026
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-14">
        <motion.form
          onSubmit={onSubmit}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-md"
          data-testid="register-form"
        >
          <div className="overline mb-3">Chapter II</div>
          <h1 className="font-serif-display text-4xl tracking-tight mb-2">
            Open your archive
          </h1>
          <p className="text-sm text-[color:var(--ink-soft)] mb-8">
            Free. Private by default. Ready for a hundred generations.
          </p>

          <label className="block mb-4">
            <span className="overline">Full name</span>
            <input
              data-testid="register-name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-3 text-lg focus:outline-none focus:border-[color:var(--rust)]"
              autoComplete="name"
            />
          </label>
          <label className="block mb-4">
            <span className="overline">Email</span>
            <input
              data-testid="register-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-3 text-lg focus:outline-none focus:border-[color:var(--rust)]"
              autoComplete="email"
            />
          </label>
          <label className="block mb-6">
            <span className="overline">Password · min 6 characters</span>
            <input
              data-testid="register-password"
              type="password"
              required
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full bg-transparent border-b border-[color:var(--ink)] py-3 text-lg focus:outline-none focus:border-[color:var(--rust)]"
              autoComplete="new-password"
            />
          </label>

          {error && (
            <div
              data-testid="register-error"
              className="mb-4 text-sm text-[color:var(--rust)] border-l-2 border-[color:var(--rust)] pl-3 py-1"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="register-submit"
            disabled={busy}
            className="btn-rust w-full justify-center disabled:opacity-50"
          >
            {busy ? "Opening…" : "Create archive"}
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-[color:var(--ink-soft)]">
            <div className="rule flex-1" />
            <span className="overline">or</span>
            <div className="rule flex-1" />
          </div>

          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/v1/auth/socialpulse/login`}
            data-testid="sso-socialpulse"
            className="btn-ghost w-full justify-center"
          >
            Continue with SocialPulse
          </a>

          <p className="mt-8 text-sm text-[color:var(--ink-soft)]">
            Already have an archive?{" "}
            <Link
              to="/app/login"
              data-testid="register-goto-login"
              className="link-underline text-[color:var(--ink)]"
            >
              Sign in
            </Link>
          </p>
        </motion.form>
      </div>
    </section>
  );
}
